/** ADMINISTRATION → ONE CONNECT → PRODUCT CONTENT — provider registry. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PlugZap, RefreshCw, Settings2, CheckCircle2, ArrowUp, ArrowDown } from "lucide-react";

const STATUS_TONE = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  SETUP_REQUIRED: "bg-amber-100 text-amber-800",
  DISABLED: "bg-slate-200 text-slate-600",
};

function CredModal({ provider, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/pc/providers/${provider.key}`, { credentials: form });
      toast.success(`${provider.name} credentials saved — provider is now ACTIVE`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center" data-testid="pc-cred-modal">
      <div className="bg-white rounded-lg p-5 w-[440px] space-y-3">
        <h3 className="font-semibold text-slate-800">Configure {provider.name}</h3>
        <p className="text-[11px] text-slate-500">{provider.notes}</p>
        {(provider.cred_fields || []).map((f) => (
          <div key={f}>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">{f.replace(/_/g, " ")}</label>
            <input type="password" value={form[f] || ""}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              data-testid={`pc-cred-${f}`}
              placeholder={provider.credentials?.[f] ? `saved: ${provider.credentials[f]}` : ""}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </div>))}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-slate-300 rounded">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="pc-cred-save"
            className="h-9 px-4 text-sm bg-emerald-700 text-white rounded disabled:opacity-50">Save & Activate</button>
        </div>
      </div>
    </div>
  );
}

export default function ProductContentProviders() {
  const [items, setItems] = useState([]);
  const [icecat, setIcecat] = useState(null);
  const [audit, setAudit] = useState(null);
  const [modal, setModal] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = useCallback(() => Promise.all([
    api.get("/pc/providers").then(({ data }) => setItems(data.items || [])),
    api.get("/pc/icecat/coverage").then(({ data }) => setIcecat(data)),
    api.get("/pc/icecat/audit/status").then(({ data }) => setAudit(data)),
  ]).catch(() => toast.error("Failed to load providers")), []);
  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/pc/icecat/audit/status")
      .then(({ data }) => setAudit(data)).catch(() => {}), 6000);
    return () => clearInterval(t);
  }, [load]);

  const runAudit = async () => {
    try {
      await api.post("/pc/icecat/audit", { per_brand: 10 });
      toast.success("Brand coverage audit started — results appear below as brands complete");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Audit failed to start"); }
  };

  const test = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post(`/pc/providers/${key}/test`);
      data.ok ? toast.success(data.message) : toast.warning(data.message);
    } catch { toast.error("Test failed"); }
    finally { setTesting(null); }
  };

  const move = async (p, dir) => {
    await api.patch(`/pc/providers/${p.key}`, { priority: p.priority + dir });
    load();
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-4" data-testid="pc-providers-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center"><PlugZap className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">ONE Product Content — Providers</h1>
          <p className="text-xs text-slate-500">Source priority is configurable. Manually approved ONE data is never silently overwritten.</p>
        </div>
        <button onClick={load} className="h-9 px-3 text-sm border border-slate-300 rounded inline-flex items-center gap-1.5" data-testid="pc-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </header>

      {icecat && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="icecat-coverage-card">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Open Icecat</span>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${icecat.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
              data-testid="icecat-status-chip">{icecat.status.replace("_", " ")}</span>
            <span className="text-[10px] text-slate-500">
              Account: {icecat.account_status === "CONFIGURED" ? "Configured" : "Not configured"}
              {icecat.access_level ? ` · Access: ${icecat.access_level.replace("_", " ")}` : ""}
              {icecat.last_successful_request ? ` · Last request ${icecat.last_successful_request.slice(0, 16).replace("T", " ")}` : ""}
              {icecat.last_product_enrichment ? ` · Last enrichment ${icecat.last_product_enrichment.slice(0, 16).replace("T", " ")}` : ""}
            </span>
            {icecat.status === "ACTIVE" && (
              <button onClick={runAudit} disabled={audit?.running} data-testid="icecat-audit-btn"
                className="ml-auto h-7 px-2.5 text-[11px] bg-emerald-700 text-white rounded disabled:opacity-50">
                {audit?.running ? "Auditing…" : "Run Brand Coverage Audit"}</button>)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 text-center">
            {[["Products checked", icecat.products_checked], ["Exact matches", icecat.exact_matches],
              ["Content available", icecat.content_available], ["Datasheet available", icecat.datasheet_available],
              ["No match", icecat.no_match], ["Restricted", icecat.restricted],
              ["Review required", icecat.review_required]].map(([l, v]) => (
              <div key={l} className="border border-slate-100 rounded p-2">
                <div className="text-lg font-bold text-slate-900">{v ?? 0}</div>
                <div className="text-[9px] uppercase text-slate-500">{l}</div>
              </div>))}
          </div>
          {audit?.latest?.report?.length > 0 && (
            <div className="mt-3 overflow-x-auto" data-testid="icecat-audit-report">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Coverage audit — {audit.latest.status} · {new Date(audit.latest.run_at).toLocaleString()}</div>
              <table className="w-full text-[11px]">
                <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Brand", "Tested", "Exact", "Specs", "Datasheet", "Images", "No match", "Restricted", "Review"].map((h) => (
                    <th key={h} className="px-2 py-1 font-semibold">{h}</th>))}
                </tr></thead>
                <tbody>
                  {audit.latest.report.map((b) => (
                    <tr key={b.brand} className="border-b border-slate-100">
                      <td className="px-2 py-1 font-medium">{b.brand}</td>
                      <td className="px-2 py-1">{b.products_tested}</td>
                      <td className="px-2 py-1 text-emerald-700 font-semibold">{b.exact_match}</td>
                      <td className="px-2 py-1">{b.specifications}</td>
                      <td className="px-2 py-1">{b.datasheet}</td>
                      <td className="px-2 py-1">{b.images}</td>
                      <td className="px-2 py-1">{b.no_match}</td>
                      <td className="px-2 py-1 text-amber-700">{b.restricted}</td>
                      <td className="px-2 py-1">{b.review_required}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>)}
        </div>)}

      <div className="space-y-2">
        {items.map((p, i) => (
          <div key={p.key} className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap"
            data-testid={`pc-provider-${p.key}`}>
            <span className="text-[11px] font-mono text-slate-400 w-6">#{p.priority}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-slate-800 text-sm">{p.name}</div>
              <div className="text-[10px] text-slate-500">{p.notes}</div>
            </div>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_TONE[p.status] || ""}`}
              data-testid={`pc-status-${p.key}`}>
              {p.status.replace("_", " ")}</span>
            <span className="inline-flex gap-1">
              <button onClick={() => move(p, -1)} disabled={i === 0}
                className="p-1 border border-slate-200 rounded disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
              <button onClick={() => move(p, 1)} disabled={i === items.length - 1}
                className="p-1 border border-slate-200 rounded disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
            </span>
            {(p.cred_fields || []).length > 0 && (
              <button onClick={() => setModal(p)} data-testid={`pc-configure-${p.key}`}
                className="h-8 px-3 text-xs border border-emerald-600 text-emerald-800 rounded inline-flex items-center gap-1">
                <Settings2 className="h-3 w-3" /> Configure</button>)}
            <button onClick={() => test(p.key)} disabled={testing === p.key}
              data-testid={`pc-test-${p.key}`}
              className="h-8 px-3 text-xs border border-slate-300 rounded inline-flex items-center gap-1 disabled:opacity-50">
              <CheckCircle2 className="h-3 w-3" /> {testing === p.key ? "Testing…" : "Test"}</button>
          </div>))}
      </div>

      {modal && <CredModal provider={modal} onClose={() => setModal(null)}
        onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
